import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Search, MapPin, Briefcase, ShieldCheck, Loader2, UserPlus, CheckCircle2,
  Clock, Building2, X
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  connected: { label: "Connected", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending: { label: "Pending", icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  revoked: { label: "Revoked", icon: X, cls: "bg-slate-100 text-slate-500 border-slate-200" },
  none: null,
};

export default function FindCAPage() {
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [specialization, setSpecialization] = useState("");
  const [inviting, setInviting] = useState(null);

  useEffect(() => { fetchDirectory(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const fetchDirectory = async (filters = {}) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ca/professionals/directory`, {
        params: filters,
        withCredentials: true,
      });
      setPros(res.data.professionals || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load directory");
    } finally {
      setLoading(false);
    }
  };

  const cities = useMemo(() => {
    return Array.from(new Set(pros.map(p => p.location).filter(Boolean))).sort();
  }, [pros]);
  const specializations = useMemo(() => {
    return Array.from(new Set(pros.map(p => p.specialization).filter(Boolean))).sort();
  }, [pros]);

  const filtered = useMemo(() => {
    let list = pros;
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(p =>
        (p.name || "").toLowerCase().includes(term) ||
        (p.firm_name || "").toLowerCase().includes(term) ||
        (p.location || "").toLowerCase().includes(term) ||
        (p.specialization || "").toLowerCase().includes(term) ||
        (p.professional_id || "").toLowerCase().includes(term)
      );
    }
    if (city) list = list.filter(p => p.location === city);
    if (specialization) list = list.filter(p => p.specialization === specialization);
    return list;
  }, [pros, search, city, specialization]);

  const handleConnect = async (pro) => {
    if (pro.connection_status === "connected") {
      toast.info("Already connected with this professional");
      return;
    }
    if (pro.connection_status === "pending") {
      toast.info("Invitation already pending");
      return;
    }
    setInviting(pro.user_id);
    try {
      await axios.post(`${API}/ca/invite`, {
        email: pro.email,
        role_of_invitee: "ca",
      }, { withCredentials: true });
      toast.success(`Invitation sent to ${pro.name || pro.email}`);
      fetchDirectory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviting(null);
    }
  };

  const initials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(p => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  };

  return (
    <div className="min-h-screen bg-slate-50 py-6 sm:py-10 px-4" data-testid="find-ca-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-50 border border-violet-100 rounded-full mb-3">
            <ShieldCheck className="w-3.5 h-3.5 text-violet-600" />
            <span className="text-xs font-semibold text-violet-700 uppercase tracking-wider">CA / Professional Directory</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Find a CA / Professional
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Discover Chartered Accountants and tax professionals on InvoiceSetu. Connect, share data securely, and let them help you with filings, accounting and compliance.
          </p>
        </div>

        {/* Search & filters */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-5" data-testid="find-ca-filters">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, firm, city, ID..."
                className="pl-10"
                data-testid="find-ca-search"
              />
            </div>
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm w-full sm:w-44"
              data-testid="find-ca-city-filter"
            >
              <option value="">All cities</option>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={specialization}
              onChange={(e) => setSpecialization(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm w-full sm:w-52"
              data-testid="find-ca-spec-filter"
            >
              <option value="">All services</option>
              {specializations.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center" data-testid="find-ca-empty">
            <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-700 mb-1">No professionals found</p>
            <p className="text-xs text-slate-500">Try a different search or filter.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="find-ca-grid">
            {filtered.map((pro, idx) => {
              const badge = STATUS_BADGE[pro.connection_status];
              return (
                <motion.div
                  key={pro.user_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(idx * 0.03, 0.3) }}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col"
                  data-testid={`find-ca-card-${pro.user_id}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-100 to-blue-100 flex items-center justify-center text-violet-700 font-bold text-sm flex-shrink-0">
                      {initials(pro.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900 truncate" data-testid={`pro-name-${pro.user_id}`}>{pro.name || "Unnamed CA"}</h3>
                        {badge && (
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border ${badge.cls}`} data-testid={`pro-status-${pro.user_id}`}>
                            <badge.icon className="w-2.5 h-2.5" />
                            {badge.label}
                          </span>
                        )}
                      </div>
                      {pro.firm_name && <p className="text-xs text-slate-500 truncate">{pro.firm_name}</p>}
                      {pro.professional_id && <p className="text-[10px] text-slate-400 font-mono mt-0.5">{pro.professional_id}</p>}
                    </div>
                  </div>

                  <div className="space-y-1.5 text-xs text-slate-600 mb-4">
                    {pro.location && (
                      <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {pro.location}</div>
                    )}
                    {pro.specialization && (
                      <div className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-slate-400" /> {pro.specialization}</div>
                    )}
                  </div>

                  {pro.bio && (
                    <p className="text-xs text-slate-500 line-clamp-3 mb-4 leading-relaxed">{pro.bio}</p>
                  )}

                  <div className="mt-auto pt-3 border-t border-slate-100">
                    {pro.connection_status === "connected" ? (
                      <Button variant="outline" disabled className="w-full text-xs" data-testid={`pro-action-${pro.user_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Connected
                      </Button>
                    ) : pro.connection_status === "pending" ? (
                      <Button variant="outline" disabled className="w-full text-xs" data-testid={`pro-action-${pro.user_id}`}>
                        <Clock className="w-3.5 h-3.5 mr-1 text-amber-600" /> Invite Pending
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleConnect(pro)}
                        disabled={inviting === pro.user_id}
                        className="w-full text-xs bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid={`pro-action-${pro.user_id}`}
                      >
                        {inviting === pro.user_id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                        ) : (
                          <UserPlus className="w-3.5 h-3.5 mr-1" />
                        )}
                        Invite to connect
                      </Button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
